import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useMemo, useState } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';

import { BgLandscapePng, BgPortraitPng } from '../../assets';
import { IconCloseSvg } from '../../assets/inventory';
import { useDevice } from '../../utils/hooks/useDevice';
import { useSafeViewport } from '../../utils/hooks/useSafeViewport';
import { useVipTicketBalance } from '../../utils/hooks/useVipTicketBalance';
import { achievementsColors } from '../Components/Achievements/achievementsTheme';
import { IndicatorScrollView, SCROLL_INDICATOR } from '../Components/common/ScrollIndicator';
import { Pressable } from '../Components/common/Touchable';
import DesktopPanelFill from '../Components/Desktop/DesktopPanelFill';
import { DuelRewardsPanel } from '../Components/Duels/DuelRewardsPanel';
import FriendsTab from '../Components/Friends/FriendsTab';
import InviteFriendsPanel from '../Components/Friends/InviteFriends/InviteFriendsPanel';
import {
	INVENTORY_INTRO,
	INVENTORY_ITEMS,
	vipTicketsItem,
} from '../Components/Inventory/inventoryCatalog';
import InventoryCategories, {
	InventoryCategoryKey,
} from '../Components/Inventory/InventoryCategories';
import InventoryItemCard, {
	ITEM_DESIGN_HEIGHT,
	ITEM_DESIGN_WIDTH,
} from '../Components/Inventory/InventoryItemCard';
import InventoryTabs, { InventoryTabKey } from '../Components/Inventory/InventoryTabs';
import {
	INVENTORY_FONT,
	inventoryColors,
	inventoryGlow,
	PANEL_WIDTH,
	RADIUS_PANEL,
} from '../Components/Inventory/inventoryTheme';
import ProfileModal from '../Components/Modals/ProfileRedesignModal';
import { getRedesignPanelFrame } from '../Components/Modals/RedesignModal';
import {
	PROFILE_SCRIM,
	useEditProfileScale,
	useProfilePanel,
} from '../Components/Profile/ProfileRedesignEditView';
import { RootStackParamList } from '../navigator';

/**
 * The modal behind the side menu's Inventory entry - Figma "Inventory / Mobile - Modal"
 * (4038:30453) and "Friends / Mobile - Modal" (4244:41053), page "PAVEL DRAFT". A scrim over
 * whatever is behind it with the Profile tab's panel (see `useProfilePanel`), so the three tabs
 * read as one modal. It is registered as a transparent-modal
 * route so the screen under it keeps painting, which is what the design draws.
 *
 * The header is shared by both frames; only the body under it changes with the tab. The friends
 * artboard is 113 taller than the inventory one and draws its panel taller to match, but the app
 * arrives at Friends by switching a tab inside a panel that is already open, so the panel keeps
 * one size and the friends body simply gets less room to scroll in - see `friendsTheme`.
 *
 * Profile is this modal's third tab; its body is `ProfileModal`, rendered inline below rather
 * than being rebuilt here - see the `tab === 'profile'` branch below.
 * The inventory body is placeholder-driven - see `inventoryCatalog`; the friends body reads the
 * friends service through the `friends` store slice.
 */
type Nav = NativeStackNavigationProp<RootStackParamList>;

// "Header" (4038:30470): the tab bar insets 12 from the panel and its tabs sit 5 into that row.
const HEADER = { tabsX: 13, tabsY: 18, closeX: 313, closeY: 13, closeSize: 44, closeIcon: 24 };
const CATEGORIES_Y = 69;
// "Profile Frame / Introduction" (4038:30487).
const INTRO = { x: 13, titleY: 145, titleSize: 22, subtitleY: 178, subtitleSize: 14 };
// "Inventory / Vertical scroll" (4038:30490) and the 334-wide grid inside it. The list runs to the
// panel's bottom edge and the indicator stops 20 short of it, so both follow the panel's height.
const SCROLL = { x: 13, y: 203, width: 334 };
// The file's own indicator is a 2-wide bar centred at x=352, from 7 under the list's top to 20
// short of the panel's bottom. The app draws the shared 4-wide Figma scroll bar in that place.
const SCROLL_BAR = { centerX: 352, y: 210, bottomInset: 20 };
const GRID_GAP = 8;
const COLUMNS = 3;

const InventoryScreen = () => {
	const { goBack } = useNavigation<Nav>();
	// Set by whatever screen routes here from outside this modal - the side menu, or the standalone
	// `Profile` route redirecting itself onto the `profile` tab. Once inside, the tab bar switches
	// tabs by local state alone (see `tab`/`setTab` below), not by navigating back here again.
	const { params } = useRoute<RouteProp<RootStackParamList, 'Inventory'>>();
	const { width, height, insets } = useSafeViewport();
	const { isDesktop } = useDevice();

	const [tab, setTab] = useState<InventoryTabKey>(params?.tab ?? 'inventory');
	const [category, setCategory] = useState<InventoryCategoryKey>(params?.category ?? 'frame');
	// Invite Friends takes over the whole panel - its own header included - while it is open, and
	// its Back lands on the friends tab it was opened from.
	const [inviteOpen, setInviteOpen] = useState(false);

	// If this modal is already open somewhere earlier in the stack, `navigate`d back into rather
	// than freshly mounted, its tab has to be picked up from the incoming params again - the
	// initial `useState` above only runs on that first mount.
	useEffect(() => {
		if (params?.tab) {
			setTab(params.tab);
			setInviteOpen(false);
		}
		if (params?.category) {
			setTab('inventory');
			setCategory(params.category);
		}
	}, [params]);

	// The Profile tab is drawn in `RedesignModal`; Inventory and Friends draw their panel here.
	// Both take the profile panel's scale and geometry, through the same placement maths, so
	// switching tabs keeps one panel in one place rather than reading as two different modals.
	const scale = useEditProfileScale();
	const profilePanel = useProfilePanel();
	const frame = getRedesignPanelFrame({
		panel: profilePanel,
		scale,
		width,
		height,
		insetTop: insets.top,
		isDesktop,
		twin: isDesktop,
	});
	// The bodies lay themselves out in design units.
	const panelHeight = frame.height / scale;
	const topInset = frame.top;
	const styles = useMemo(
		() => createStyles(scale, panelHeight, topInset, frame.left),
		[panelHeight, scale, topInset, frame.left],
	);

	// The same backdrop MainLayout paints, so the modal agrees with the app behind it.
	const backdrop = isDesktop ? BgLandscapePng : BgPortraitPng;

	// The VIP Tickets card leads Featured once the wallet has answered; logged out it stays hidden.
	const vipTickets = useVipTicketBalance();
	const items =
		category === 'featured' && vipTickets != null
			? [vipTicketsItem(vipTickets), ...INVENTORY_ITEMS.featured]
			: INVENTORY_ITEMS[category];
	const intro = INVENTORY_INTRO[category];

	// Profile is this modal's third tab; its body lives in `ProfileModal` rather than being rebuilt
	// here, but rendered inline (no `renderInline={false}` native <Modal>, no navigation) so it
	// stays the one screen this whole modal already is instead of becoming a second one - on iOS a
	// screen swap here is a real native modal present/dismiss, which read as the tabs "reopening"
	// on every switch. `onSelectTab` hands its own Inventory/Friends taps back to this same state.
	if (tab === 'profile') {
		return (
			<ProfileModal onClose={goBack} renderInline onSelectTab={setTab} initialEdit={params?.edit} />
		);
	}

	return (
		<View style={styles.root}>
			{/* Figma "Backdrop / Home / Mobile" (4038:30454): the frame paints its own backdrop under
			    the scrim rather than letting the screen behind show through, so none of that screen's
			    own content - its header cards, its nav - reads around the panel. */}
			{/* The desktop artboards float the panel over the live screen instead, under the same
			    scrim the Profile tab has (not blurred, to match it). */}
			{isDesktop ? null : (
				<Image
					source={backdrop}
					style={[styles.screenBackdrop, { width, height }]}
					resizeMode='cover'
				/>
			)}
			<View pointerEvents='none' style={styles.scrim} />

			{/* Tapping outside closes the modal, the way every other overlay in the app does. */}
			<Pressable accessibilityRole='button' style={StyleSheet.absoluteFill} onPress={goBack} />

			<View style={styles.panel}>
				{/* "Modal / Inventory" carries no fill: on the artboard it sits over a static
				    "Backdrop / Home / Mobile", so its interior is the app's red texture. Painting that
				    backdrop here - sized to the viewport and offset by the panel's own position, the
				    way the event screen's fades do - reproduces that on any route, instead of letting
				    the live screen's header cards read through the panel. */}
				{isDesktop ? (
					<DesktopPanelFill radius={RADIUS_PANEL * scale} scale={scale} />
				) : (
					<View pointerEvents='none' style={styles.backdropClip}>
						<Image
							source={backdrop}
							style={[styles.backdrop, { width, height }]}
							resizeMode='cover'
						/>
					</View>
				)}
				{inviteOpen && tab === 'friends' ? (
					<InviteFriendsPanel
						scale={scale}
						panelHeight={panelHeight}
						onBack={() => setInviteOpen(false)}
						onClose={goBack}
					/>
				) : (
					<>
						<View style={styles.tabs}>
							<InventoryTabs value={tab} onChange={setTab} scale={scale} />
						</View>

						<Pressable accessibilityRole='button' onPress={goBack} style={styles.close}>
							{/* Wrapped for the same painting-order reason as the tab icons. */}
							<View>
								<IconCloseSvg width={HEADER.closeIcon * scale} height={HEADER.closeIcon * scale} />
							</View>
						</Pressable>
					</>
				)}

				{inviteOpen && tab === 'friends' ? null : tab === 'friends' ? (
					<FriendsTab
						scale={scale}
						panelHeight={panelHeight}
						onInvite={() => setInviteOpen(true)}
					/>
				) : (
					<>
						<View style={styles.categories}>
							<InventoryCategories value={category} onChange={setCategory} scale={scale} />
						</View>

						<Text numberOfLines={1} style={styles.introTitle}>
							{intro.title}
						</Text>
						<Text numberOfLines={1} style={styles.introSubtitle}>
							{intro.subtitle}
						</Text>

						{/* "Scroll indicator" (4038:30583/30584) sits just right of the 334-wide grid, so the
						    bar is pushed past the list's own right edge and the grid keeps its width. */}
						<IndicatorScrollView
							style={styles.scroll}
							contentContainerStyle={styles.scrollContent}
							indicatorInsets={{
								top: (SCROLL_BAR.y - SCROLL.y) * scale,
								bottom: SCROLL_BAR.bottomInset * scale,
								right:
									(SCROLL.x + SCROLL.width - SCROLL_BAR.centerX) * scale -
									SCROLL_INDICATOR.width / 2,
							}}
							reserveGutter={false}
						>
							{category === 'rewards' ? (
								<DuelRewardsPanel
									width={(ITEM_DESIGN_WIDTH * COLUMNS + GRID_GAP * (COLUMNS - 1)) * scale}
									scale={scale}
								/>
							) : (
								items.map(item => <InventoryItemCard key={item.key} item={item} scale={scale} />)
							)}
						</IndicatorScrollView>
					</>
				)}
			</View>
		</View>
	);
};

const createStyles = (scale: number, panelHeight: number, topInset: number, panelLeft: number) =>
	StyleSheet.create({
		root: {
			flex: 1,
			alignItems: 'center',
		},
		screenBackdrop: {
			position: 'absolute',
			left: 0,
			top: 0,
		},
		scrim: {
			...StyleSheet.absoluteFillObject,
			backgroundColor: PROFILE_SCRIM,
		},
		panel: {
			position: 'absolute',
			top: topInset,
			width: PANEL_WIDTH * scale,
			height: panelHeight * scale,
			borderRadius: RADIUS_PANEL * scale,
			borderWidth: Math.max(1, 1 * scale),
			// The profile tab's panel (RedesignModal) carries the paler gold; all three tabs share it so
			// the rim does not change colour as the tabs switch.
			borderColor: achievementsColors.panelBorder,
			overflow: 'hidden',
			...inventoryGlow.panel,
		},
		// The panel's own `overflow: hidden` does not reliably clip this oversized child to the
		// rounded corners once the panel also carries a shadow - the backdrop's square corners read
		// through the curve at the top. Its own rounded clip is what keeps the corners clean.
		backdropClip: {
			...StyleSheet.absoluteFillObject,
			borderRadius: RADIUS_PANEL * scale,
			overflow: 'hidden',
		},
		backdrop: {
			position: 'absolute',
			left: -panelLeft,
			top: -topInset,
		},
		tabs: {
			position: 'absolute',
			left: HEADER.tabsX * scale,
			top: HEADER.tabsY * scale,
		},
		close: {
			position: 'absolute',
			left: HEADER.closeX * scale,
			top: HEADER.closeY * scale,
			width: HEADER.closeSize * scale,
			height: HEADER.closeSize * scale,
			alignItems: 'center',
			justifyContent: 'center',
		},
		categories: {
			position: 'absolute',
			left: 0,
			top: CATEGORIES_Y * scale,
		},
		// Figma "Heading/H2": Sofia Sans Bold 22, line-height 1.3.
		introTitle: {
			position: 'absolute',
			left: INTRO.x * scale,
			top: INTRO.titleY * scale,
			color: inventoryColors.gold50,
			fontFamily: INVENTORY_FONT,
			fontSize: INTRO.titleSize * scale,
			fontWeight: '700',
			lineHeight: INTRO.titleSize * 1.3 * scale,
		},
		// Figma "Body/Pill": Sofia Sans Regular 14, line-height 14.
		introSubtitle: {
			position: 'absolute',
			left: INTRO.x * scale,
			top: INTRO.subtitleY * scale,
			color: inventoryColors.gold50,
			fontFamily: INVENTORY_FONT,
			fontSize: INTRO.subtitleSize * scale,
			fontWeight: '400',
			lineHeight: INTRO.subtitleSize * scale,
		},
		scroll: {
			position: 'absolute',
			left: SCROLL.x * scale,
			top: SCROLL.y * scale,
			width: SCROLL.width * scale,
			height: (panelHeight - SCROLL.y) * scale,
		},
		// The grid is three 106-wide cells with 8 between them, wrapping into 132-tall rows.
		scrollContent: {
			flexDirection: 'row',
			flexWrap: 'wrap',
			width: (ITEM_DESIGN_WIDTH * COLUMNS + GRID_GAP * (COLUMNS - 1)) * scale,
			gap: GRID_GAP * scale,
			paddingBottom: ITEM_DESIGN_HEIGHT * 0.25 * scale,
		},
	});

export default InventoryScreen;
